import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { FILM_SPRING, useFilmTyping } from "@shared/FilmMotion/FilmMotion";
import "@shared/FilmMotion/FilmMotion.css";
import "./MockCaseTimeline.css";
// Same icons as the workspace docket (packages/app-core/src/assets/images/atticus/icons).
import plaintiffIcon from "@icons/docket/document-red-icon.svg";
import defendantIcon from "@icons/docket/document-blue-icon.svg";
import courtIcon from "@icons/docket/judge-black-icon.svg";
import fileIcon from "@icons/docket/file-with-background.svg";
import summarizeIcon from "@icons/docket/add-summarize.svg";
import moreIcon from "@icons/docket/more-line.svg";
import { mockData, SEED } from "./MockData";

const VISIBLE = 2;
const GROUP_ICON = { plaintiff: plaintiffIcon, defendant: defendantIcon, court: courtIcon };
const ROLE_LABEL = { plaintiff: "Plaintiff", defendant: "Defendant", court: "Court" };

// One filing, as film beats (ms): the docket is quiet; the "New docket entries" notice drops in; the entry
// lands on top of the timeline; Summarize is pressed, loads, and the summary types out; the notice is marked
// read and folds away; hold, then the next filing.
const BEATS = { idle: 1400, notify: 800, land: 1000, press: 450, loading: 900, summary: 2300, read: 700, hold: 1500 };
const ORDER = ["idle", "notify", "land", "press", "loading", "summary", "read", "hold"];

// The workspace truncates document titles at 25 characters (DocumentItem).
const clip = (title) => (title.length > 25 ? `${title.slice(0, 25)}...` : title);

function withEcfLinks(text) {
    return text.split(/(ECF No\. \d+)/).map((part, i) => (i % 2 ? <span key={i} className="dk-ecf-link">{part}</span> : part));
}

function SummaryText({ text, active }) {
    const typed = useFilmTyping(text, active, 1500);
    return <>{active ? typed : text}</>;
}

/*
 * Marketing copy of the workspace Docket Timeline (packages/app-core CaseTimeline / TimelineItem /
 * DocumentItem / InlineNotifications), animated the way the motion films stage a beat: nothing scrolls,
 * the new filing arrives on its own, and what Vienna does with it (the summary) gets the last moment.
 * `seed` is how many entries are already on the docket when the card opens. Pauses off screen; static for
 * reduced motion.
 */
const MockCaseTimeline = ({ seed = SEED }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-60px" });
    const reduce = useReducedMotion();
    const [filed, setFiled] = useState(seed);
    const [phase, setPhase] = useState("idle");

    useEffect(() => {
        if (reduce || !inView) return undefined;
        const id = setTimeout(() => {
            if (phase === "idle" && filed >= mockData.length) {
                setFiled(seed);
                return;
            }
            const next = ORDER[(ORDER.indexOf(phase) + 1) % ORDER.length];
            if (next === "land") setFiled((n) => n + 1);
            setPhase(next);
        }, BEATS[phase]);
        return () => clearTimeout(id);
    }, [phase, filed, inView, reduce, seed]);

    const shownFiled = reduce ? mockData.length : filed;
    const groups = mockData.slice(Math.max(0, shownFiled - VISIBLE), shownFiled).reverse();
    const fresh = groups[0];
    const after = (p) => ORDER.indexOf(phase) >= ORDER.indexOf(p);
    // The newest entry carries this cycle's beats; earlier ones already show their summaries.
    const freshIsLive = !reduce && phase !== "idle" && after("land");
    const noticeOpen = !reduce && (phase === "notify" || (after("land") && !after("read")));
    const pending = !reduce && phase === "notify" ? mockData[filed] : null;
    const noticeEntry = pending || fresh;

    const summarizeState = (item) => {
        if (reduce || item !== fresh || !freshIsLive) return "done";
        if (phase === "land") return "ready";
        if (phase === "press") return "pressed";
        if (phase === "loading") return "loading";
        return "done";
    };

    return (
        <div className="dk" ref={ref}>
            <AnimatePresence initial={false}>
                {noticeOpen && noticeEntry ? (
                    <motion.section
                        key="notice"
                        className="dk-notice"
                        initial={{ opacity: 0, height: 0, y: -10 }}
                        animate={{ opacity: 1, height: "auto", y: 0 }}
                        exit={{ opacity: 0, height: 0, y: -6, transition: { duration: 0.35 } }}
                        transition={FILM_SPRING}
                    >
                        <div className="dk-notice__inner">
                            <div className="dk-notice__title">New docket entries</div>
                            <div className="dk-notice__row">
                                <span className="dk-notice__count">1</span>
                                <span className="dk-notice__text">ECF No. {noticeEntry.ecf} · {clip(noticeEntry.title)}</span>
                                <span className={`dk-notice__read ${phase === "read" ? "is-pressed" : ""}`}>Mark read</span>
                            </div>
                        </div>
                    </motion.section>
                ) : null}
            </AnimatePresence>

            <ol className="dk-items">
                <AnimatePresence initial={false} mode="popLayout">
                    {groups.map((item) => {
                        const isFresh = item === fresh && freshIsLive;
                        const state = summarizeState(item);
                        return (
                            <motion.li
                                key={item.id}
                                layout={!reduce}
                                className={`dk-group ${isFresh && phase === "land" ? "is-landing" : ""}`}
                                initial={reduce ? false : { opacity: 0, y: -26, rotate: -0.8, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 18, scale: 0.97, transition: { duration: 0.3 } }}
                                // A new filing waits for the older ones to make room before it drops in.
                                transition={{ ...FILM_SPRING, delay: isFresh && phase === "land" ? 0.22 : 0, opacity: { duration: 0.25, delay: isFresh && phase === "land" ? 0.22 : 0 } }}
                            >
                                <img className="dk-icon" src={GROUP_ICON[item.classification]} alt="" />
                                <div className="dk-body">
                                    <div className="dk-head">
                                        <span className="dk-date">{item.date} | Docket Entry # {item.seq} | ECF No. {item.ecf}</span>
                                        <span className="dk-role"><span>{ROLE_LABEL[item.classification]}</span><span className="dk-stage">{item.stage}</span></span>
                                    </div>
                                    <div className="dk-card">
                                        <div className="dk-file">
                                            <span className="dk-file__left">
                                                <img className="dk-file__icon" src={fileIcon} alt="" />
                                                <span className="dk-file__title">{clip(item.title)}</span>
                                            </span>
                                            <span className="dk-file__right">
                                                <span className={`dk-summarize is-${state}`}>
                                                    {state === "done" ? null : <img src={summarizeIcon} alt="" />}
                                                    {state === "loading" ? "Loading..." : state === "done" ? "Summary" : "Summarize"}
                                                    {state === "pressed" ? <span className="film-ripple" style={{ left: "50%", top: "50%" }} /> : null}
                                                </span>
                                                <img className="dk-more" src={moreIcon} alt="" />
                                            </span>
                                        </div>
                                        <div className="dk-desc">{withEcfLinks(item.description)}</div>
                                        <AnimatePresence initial={false}>
                                            {state === "done" && (!isFresh || after("summary")) ? (
                                                <motion.div
                                                    key="summary"
                                                    className="dk-summary"
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                                >
                                                    <span className="dk-summary__by">Vienna</span>
                                                    <SummaryText text={item.summary} active={isFresh && phase === "summary"} />
                                                </motion.div>
                                            ) : null}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </motion.li>
                        );
                    })}
                </AnimatePresence>
            </ol>
        </div>
    );
};

export default MockCaseTimeline;
